import { useState, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Grid,
  Alert
} from '@mui/material';

interface ContactDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmitContact: (data: {
    name: string;
    email: string;
    message: string;
  }) => Promise<void>;
  loading: boolean;
  error: string | null;
}

interface FormValues {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const ContactDialog = ({
  open,
  onClose,
  onSubmitContact,
  loading,
  error
}: ContactDialogProps) => {
  const [values, setValues] = useState<FormValues>({
    name: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const tempErrors: FormErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!values.name.trim()) tempErrors.name = 'Name is required.';
    if (!values.email.trim()) {
      tempErrors.email = 'Email is required.';
    } else if (!emailRegex.test(values.email)) {
      tempErrors.email = 'Please enter a valid email address.';
    }
    if (!values.message.trim()) tempErrors.message = 'Message cannot be empty.';

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Fires wrapper method -> attempts backend API entry -> clears local states if resolve returns clean
    await onSubmitContact(values);

    // Clear out text values only if successful (handled by parent context resetting dialog views)
    if (!error) {
      setValues({ name: '', email: '', message: '' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.4rem',
          color: 'primary.main',
          borderBottom: '2px solid #eee',
          pb: 2
        }}
      >
        Contact Us
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* 🚀 Displays BE API Response Failures directly within the modal frame */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Your Name"
              name="name"
              value={values.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              margin="normal"
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={values.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Message"
          name="message"
          value={values.message}
          onChange={handleChange}
          error={!!errors.message}
          helperText={errors.message}
          margin="normal"
          multiline
          rows={4}
          disabled={loading}
        />

        <Button
          variant="contained"
          fullWidth
          disabled={loading}
          onClick={handleSubmit}
          sx={{
            mt: 2,
            py: 1.4,
            fontSize: '1rem',
            textTransform: 'none'
          }}
        >
          {loading ? 'Sending Request...' : 'Send Message'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
